import { FC, useMemo } from 'react';

import { FooterProps as PurPurFooterProps } from '@purpur/library';
import { facebook } from '@purpur/library/icon/assets/facebook';
import { instagram } from '@purpur/library/icon/assets/instagram';
import { linkedin } from '@purpur/library/icon/assets/linkedin';
import { snapchat } from '@purpur/library/icon/assets/snapchat';
import { whatsapp } from '@purpur/library/icon/assets/whatsapp';
import { x } from '@purpur/library/icon/assets/x';
import { youtube } from '@purpur/library/icon/assets/youtube';

import { KeyboardCode } from '@telialt/common-constants/src/keyboard-codes';
import { KeyboardKey } from '@telialt/common-constants/src/keyboard-keys';
import { getLocalSvgIcon } from '@telialt/common-icon/src';
import { NavigationItem } from '@telialt/models/src/Navigation';

import { ariaLabels, ONETRUST_COOKIE_SETTING_BUTTON_CLASSNAME, ONETRUST_COOKIE_SETTING_TEST_ID } from './constants';
import { StyledFooter } from './Footer.styles';
import { FooterAppPromotion } from './FooterAppPromotion/FooterAppPromotion';
import { FOOTER_TEST_IDS } from './test-ids';

type Props = {
    socialMenu?: NavigationItem[];
    mainMenu?: NavigationItem[];
    additionalMenu?: NavigationItem[];
    copyrightText?: string;
};

const SOCIAL_ICONS = {
    facebook,
    instagram,
    linkedin,
    snapchat,
    whatsapp,
    x,
    youtube,
};

const FALLBACK_SOCIAL_ICON = { name: '', svg: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" />' };

const getSocialIcon = (iconName?: string) => {
    if (!iconName) {
        return FALLBACK_SOCIAL_ICON;
    }

    return SOCIAL_ICONS[iconName as keyof typeof SOCIAL_ICONS] ?? getLocalSvgIcon(iconName);
};

declare global {
    interface Window {
        OneTrust?: {
            ToggleInfoDisplay?: () => void;
        };
    }
}

export const Footer: FC<Props> = ({ socialMenu, mainMenu, additionalMenu, copyrightText }) => {
    const social: PurPurFooterProps['social'] = useMemo(
        () =>
            socialMenu && socialMenu?.length !== 0
                ? {
                      sectionProps: {
                          'aria-label': ariaLabels.social,
                          'data-test': FOOTER_TEST_IDS.SOCIAL,
                          'data-section': 'Other',
                      },
                      links: socialMenu.map((socialLink) => ({
                          href: socialLink.url,
                          target: socialLink.openInNewTab ? '_blank' : undefined,
                          rel: socialLink.noFollow ? 'nofollow' : undefined,
                          icon: getSocialIcon(socialLink.icon),
                          'aria-label': socialLink.title,
                          'data-element': 'Link',
                          'data-element-name': socialLink.title,
                      })),
                  }
                : undefined,
        [socialMenu],
    );

    const navigation: PurPurFooterProps['navigation'] = useMemo(
        () =>
            mainMenu && mainMenu.length !== 0
                ? {
                      sectionProps: {
                          'aria-label': ariaLabels.navigation,
                          'data-test': FOOTER_TEST_IDS.NAVIGATION,
                          'data-section': 'Mega Menu',
                      },
                      links: mainMenu.map((navigationLink) => ({
                          heading: navigationLink.title,
                          links: navigationLink.children.map((link) => ({
                              href: link.url,
                              text: link.title,
                              target: link.openInNewTab ? '_blank' : undefined,
                              rel: link.noFollow ? 'nofollow' : undefined,
                              disableVisited: true,
                              'data-element': 'Link',
                              'data-element-name': link.title,
                          })),
                      })),
                  }
                : undefined,
        [mainMenu],
    );

    const contacts: PurPurFooterProps['contacts'] = useMemo(
        () => ({
            sectionProps: { 'aria-label': ariaLabels.contacts, 'data-test': FOOTER_TEST_IDS.CONTACTS },
            copyright: copyrightText,
        }),
        [copyrightText],
    );

    const additional: PurPurFooterProps['additional'] = useMemo(
        () => ({
            sectionProps: {
                'aria-label': ariaLabels.additional,
                'data-test': FOOTER_TEST_IDS.ADDITIONAL,
                'data-section': 'Other',
            },
            links: additionalMenu?.map((legalLink) => {
                return {
                    href: legalLink.url,
                    text: legalLink.title,
                    target: legalLink.openInNewTab ? '_blank' : undefined,
                    rel: legalLink.noFollow ? 'nofollow' : undefined,
                    disableVisited: true,
                    'data-element': 'Link',
                    'data-element-name': legalLink.title,
                    ...(legalLink.component === 'ONETRUST' && {
                        onClick: undefined,
                        href: null, // a hack to prevent PurPur's Link component from rendering <a> tag, but still assigning a key when iterating
                        text: (
                            <span>
                                <a
                                    data-test={ONETRUST_COOKIE_SETTING_TEST_ID}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        window.OneTrust?.ToggleInfoDisplay?.();
                                    }}
                                    className={ONETRUST_COOKIE_SETTING_BUTTON_CLASSNAME}
                                    onKeyDown={(e) => {
                                        if (e.key === KeyboardKey.Enter || e.code === KeyboardCode.Space) {
                                            e.currentTarget.click();
                                            e.preventDefault();
                                        }
                                    }}
                                    role="button"
                                    tabIndex={0}
                                >
                                    {legalLink.title}
                                </a>
                            </span>
                        ) as any as string, // Hack to drill JSX.Element inside Purpur's Link component
                    }),
                };
            }),
        }),
        [additionalMenu],
    );

    return (
        <>
            <StyledFooter
                data-test={FOOTER_TEST_IDS.FOOTER}
                data-section="Footer"
                social={social}
                navigation={navigation}
                contacts={contacts}
                additional={additional}
                $contactSectionAriaLabel={ariaLabels.contacts}
            />
            <FooterAppPromotion />
        </>
    );
};
